import LocationCardResult from "./LocationCardResult";
import PersonCardResult from "./PersonCardResult";
import ProductCardResult from "./ProductCardResult";
import React from "react";
import TextualCardResult from "./TextualCardResult";
import { createUseStyles } from "react-jss";
import { detect } from "detect-browser";
import { eventTypes } from "../../../lib/analytics";
import { openLinksByLocation } from "../../../lib/utils";

const useStyles = createUseStyles((theme) => ({
  cardHover: {
    boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
    transform: "translate(0, -5px)",
    cursor: "pointer",
  },
  cardWidth: { width: "230px" },
  card: {
    composes: "$cardWidth",
    flexDirection: "row",
    backgroundColor: "white",
    margin: "5px",
    padding: "15px",
    borderRadius: "8px",
    overflow: "hidden",
    "-ms-overflow-style": "none",
    transition: "200ms ease-in-out",
    "&:hover $visitPageLink": { visibility: "visible" },
    "&:hover": {
      boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
      transform: "translate(0, -5px)",
      cursor: "pointer",
    },
  },
  cardResultWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "295px",
    maxHeight: "295px",
    margin: "0 !important",
  },
  cardUpperContentWrapper: {
    height: "100%",
    marginBottom: "10px",
  },
  visitPageLink: {
    visibility: "hidden",
    color: theme.cardTextColorSecondary,
    display: "flex",
    alignItems: "center",
    position: "absolute",
    top: "5px",
    right: "0",
    fontSize: "12px",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
  visitPageIcon: {
    marginLeft: "5px",
    height: "12px",
    stroke: theme.secondaryColor,
    fill: theme.secondaryColor,
  },
  cardContentWrapper: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },
  // Some CSS hacks to create the hover darkness effect, since we get the palette variable as static CSS and can't use SASS's darken()
  actionWrapper: {
    display: "grid",
  },
  actionButton: {
    gridRow: "1",
    gridColumn: "1",
    zIndex: "0",
    height: "35px !important",
    background: theme.secondaryColor,
    border: "0",
    borderRadius: "5px",
    fontStyle: "normal",
    fontWeight: "normal",
    fontSize: "16px",
    cursor: "pointer",
    "&:hover": { filter: "brightness(0.9)" },
    "&:active": { filter: "brightness(0.8)" },
  },
  actionButtonText: {
    gridRow: "1",
    gridColumn: "1",
    color: "white",
    placeSelf: "center",
    zIndex: "1",
    cursor: "pointer",
  },
  "actionButtonText:hover ~ .actionButton": { filter: "brightness(0.9)" },
  "actionButtonText:active ~ .actionButton": { filter: "brightness(0.8)" },

  "@media only screen and (max-width: 330px)": {
    // very small screen - probably only iPhone 5/SE
    maxWidth: "270px",
  },
}));

const MainButton = ({ suggestion, styles, onClick }) =>
  detect().name !== "ie" ? (
    <div className={styles.actionWrapper}>
      <div className={styles.actionButtonText} onClick={onClick(suggestion)}>
        {suggestion.displayText}
      </div>
      <button className={styles.actionButton} onClick={onClick(suggestion)} />
    </div>
  ) : (
    <button
      className={[styles.actionButton, styles.actionButtonText].join(" ")}
      onClick={onClick(suggestion)}
    >
      {suggestion.displayText}
    </button>
  );

const clickHandler = (onSuggestionClick) => (suggestion) => (event) => {
  event.stopPropagation();
  onSuggestionClick(suggestion);
};

export default ({
  result,
  blockClick,
  sendEvent,
  onSuggestionClick,
  onMapClick,
  onHoverToggle,
  shouldHover = false,
  isSelected = false,
}) => {
  const pageClick = (event) => {
    event.stopPropagation();
    if (blockClick || !result.url) {
      return;
    }
    sendEvent(eventTypes.RESULT_CLICK, result);
    openLinksByLocation(result.url);
  };

  // TODO(nur): change default once all bots specify content type.
  const ContentComponent = {
    PERSON: PersonCardResult,
    LOCATION: LocationCardResult,
    TEXTUAL: TextualCardResult,
    PRODUCT: ProductCardResult,
  }[result.contentType || "PERSON"];

  const styles = useStyles();
  const mainSuggestion = result.suggestions.find(
    (x) => x.sayText || x.phone || x.url
  );
  return (
    <div
      className={[
        styles.card,
        shouldHover || isSelected ? styles.cardHover : "",
      ].join(" ")}
      onClick={pageClick}
      onMouseEnter={onHoverToggle ? () => onHoverToggle(true) : null}
      onMouseLeave={onHoverToggle ? () => onHoverToggle(false) : null}
    >
      <div className={styles.cardResultWrapper}>
        <div className={styles.cardContentWrapper}>
          <div className={styles.cardUpperContentWrapper}>
            {["PERSON", "TEXTUAL", "LOCATION"].includes(result.contentType) && (
              <div className={styles.visitPageLink} onClick={pageClick}>
                Visit page
                <div>
                  <svg
                    className={styles.visitPageIcon}
                    width={12}
                    height={12}
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 5L11.3785 0.62146"
                      strokeWidth="1.15908"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7 0.62146H11.3785V5"
                      strokeWidth="1.15908"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M4.65378 0H3C1.34315 0 0 1.34315 0 3V9C0 10.6569 1.34315 12 3 12H9C10.6569 12 12 10.6569 12 9V7.34755H11V9C11 10.1046 10.1046 11 9 11H3C1.89543 11 1 10.1046 1 9V3C1 1.89543 1.89543 1 3 1H4.65378V0Z"
                    />
                  </svg>
                </div>
              </div>
            )}
            <ContentComponent
              result={result}
              suggestionClick={clickHandler(onSuggestionClick)}
              onMapClick={onMapClick}
            />
          </div>
        </div>
        {mainSuggestion && (
          <MainButton
            onClick={clickHandler(onSuggestionClick)}
            styles={styles}
            suggestion={mainSuggestion}
          />
        )}
      </div>
    </div>
  );
};
